<template>
  <div v-if="planEditable" class="container-fluid py-3">
    <!-- ENCABEZADO -->
    <div class="d-flex flex-column flex-md-row justify-content-between align-items-start mb-3">
      <div>
        <h4 class="fw-bold">{{ planEditable.objetivo }}</h4>
        <p class="text-muted mb-1">Autor: {{ planEditable.planificado_por }}</p>
      </div>

      <div class="d-flex gap-2 mt-2 mt-md-0">
        <button class="btn btn-outline-primary btn-sm" @click="toggleEdicion">
          <font-awesome-icon :icon="modoEdicion ? 'floppy-disk' : 'fas fa-pen'" />
          {{ modoEdicion ? 'Guardar' : 'Editar' }}
        </button>
        <button class="btn btn-outline-danger btn-sm" @click="eliminarPlan">
          <font-awesome-icon icon="fas fa-trash" /> Eliminar
        </button>
      </div>
    </div>

    <!-- DETALLES -->
    <div class="mb-4">
      <PlanDetalles :plan="planEditable" :editable="modoEdicion" @guardar="actualizarPlan"
        @cancelar="cancelarEdicion" />
    </div>

    <!-- COSTOS -->
    <div class="mb-4">
      <CostosPorEtapa :costos="planEditable.costo_estimado_por_etapa" :estadisticas="estadisticasEtapa" />
    </div>

    <!-- TAREAS -->
    <div class="mt-4">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h5 class="mb-0"><font-awesome-icon :icon="['fas', 'list-check']" class="me-2" /> Tareas Planificadas</h5>
        <button class="btn btn-success btn-sm" @click="toggleFormularioNuevaActividad"
          v-if="!mostrarFormularioNuevaActividad">
          <font-awesome-icon icon="fas fa-plus" /> Nueva Actividad
        </button>
      </div>

      <!-- Modal para Nueva Actividad -->
      <div v-if="mostrarFormularioNuevaActividad" class="modal fade show d-block" tabindex="-1"
        style="background-color: rgba(0,0,0,0.5);">
        <div class="modal-dialog">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Nueva Actividad</h5>
              <button type="button" class="btn-close" @click="cerrarModal"></button>
            </div>
            <div class="modal-body">
              <NuevaActividad :plan-id="planEditable.id" :eventos="eventos" :categorias="categorias"
                :ultimoNumeroEtapa="ultimoNumeroEtapa" @actividad-agregada="agregarActividad" @cancelar="cerrarModal" />
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="cerrarModal">Cerrar</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Tareas agrupadas por etapa -->
      <div class="tareas-por-etapa">
        <div v-for="(grupo, index) in tareasAgrupadas" :key="index" class="mb-5">
          <!-- Header de la etapa -->
          <h5 class="etapa-titulo d-flex align-items-center gap-2 mb-3">
            <font-awesome-icon :icon="['fas', 'seedling']" class="text-success" />
            <span>Etapa: {{ grupo.etapa }}</span>
            <span class="badge bg-light text-muted fw-normal ms-2">
              {{ grupo.actividades.length }} tarea(s)
            </span>
          </h5>

          <!-- Grilla flexible -->
          <div class="row gy-4 gx-3">
            <div v-for="actividad in grupo.actividades" :key="actividad.id" class="col-12 col-md-6 col-lg-4">
              <TareaDelPlan :actividad="actividad" :editable="modoEdicion" @guardar="actualizarActividad"
                @eliminar="eliminarActividad" />
            </div>
          </div>
        </div>
      </div>


    </div>
  </div>

  <div v-else>
    <p class="text-muted">Seleccione un plan para ver sus detalles.</p>
  </div>
</template>




<script>
import axios from '@/axios';
import PlanDetalles from './PlanDetalles.vue';
import TareaDelPlan from './TareaDelPlan.vue';
import CostosPorEtapa from './CostosPorEtapa.vue';
import NuevaActividad from './crearPlan/NuevaActividad.vue';
import emitter from '@/mitt';




export default {
  components: {
    PlanDetalles,
    CostosPorEtapa,
    NuevaActividad,
    TareaDelPlan,
  },
  props: {
    plan: {
      type: Object,
      required: false,
    },
  },


  data() {
    return {
      planEditable: null,
      etapaActual: 0,
      modoEdicion: false,
      mostrarFormularioNuevaActividad: false,
      eventos: [],
      categorias: [],
      clubActual: null,
    };
  },
  computed: {
    etapas() {
      return this.planEditable?.plan_cultivo_actividades
        ? [...new Set(this.planEditable.plan_cultivo_actividades.map((actividad) => actividad.etapa_evento_cultivo))]
        : [];
    },
    progreso() {
      return (this.etapaActual / this.etapas.length) * 100;
    },
    actividadesFiltradas() {
      return this.planEditable?.plan_cultivo_actividades
        ? this.etapaActual === 0
          ? this.planEditable.plan_cultivo_actividades
          : this.planEditable.plan_cultivo_actividades.filter(
            (actividad) => actividad.etapa_evento_cultivo === this.etapas[this.etapaActual - 1]
          )
        : [];
    },
    estadisticasEtapa() {
      const etapas = this.planEditable?.costo_estimado_por_etapa || {};
      const totalTareas = this.planEditable?.plan_cultivo_actividades?.length || 0;
      const costos = Object.values(etapas);
      const totalCostos = costos.reduce((acc, costo) => acc + costo, 0);
      const maxCosto = Math.max(...costos);
      const etapaMasCostosa = Object.keys(etapas).find((key) => etapas[key] === maxCosto);
      const promedioCostoEtapa = costos.length ? (totalCostos / costos.length).toFixed(2) : 0;

      return {
        etapaMasCostosa,
        totalCostos,
        totalTareas,
        promedioCostoEtapa,
      };
    },
    tareasAgrupadas() {
      if (!this.planEditable?.plan_cultivo_actividades) return [];

      const agrupadas = [];
      let grupoActual = null;
      let ultimaEtapa = null;

      // Ordenamos primero por el campo `orden`
      const actividadesOrdenadas = [...this.planEditable.plan_cultivo_actividades].sort(
        (a, b) => a.orden - b.orden
      );

      for (const actividad of actividadesOrdenadas) {
        const etapa = actividad.etapa_evento_cultivo;
        if (!grupoActual || etapa !== ultimaEtapa) {
          grupoActual = {
            etapa,
            actividades: [],
          };
          agrupadas.push(grupoActual);
          ultimaEtapa = etapa;
        }
        grupoActual.actividades.push(actividad);
      }

      return agrupadas;
    },
    ultimoNumeroEtapa() {
      const etapas = this.planEditable?.plan_cultivo_actividades.map(a => a.numero_etapa) || [];
      return Math.max(0, ...etapas);
    },
  },
  methods: {
    toggleEdicion() {
      this.modoEdicion = !this.modoEdicion;
    },
    async actualizarPlan(planActualizado) {
      try {
        await axios.put(`/api/planes_cultivo/${this.planEditable.id}`, { form: planActualizado, club_id: this.clubActual.id });
        alert('Plan actualizado exitosamente');
        this.planEditable = { ...this.planEditable, ...planActualizado };
        this.modoEdicion = false; // Cierra la edición al guardar
      } catch (error) {
        console.error('Error al actualizar el plan:', error);
        alert(error.response.data.error || 'Error al actualizar el plan');
      }
    },
    async actualizarActividad(actividad) {
      try {
        await axios.put(`/api/planes_cultivo_actividades/${actividad.id}`, { form: actividad, club_id: this.clubActual.id });
        alert('Actividad actualizada exitosamente');
        this.recargarPlan();
      } catch (error) {
        console.error('Error al actualizar la actividad:', error);
        alert(error.response.data.error || 'Error al actualizar la actividad');
      }
    },
    async eliminarActividad(id) {
      if (confirm('¿Estás seguro de eliminar esta actividad?')) {
        try {
          await axios.delete(`/api/planes_cultivo_actividades/${id}`, { data: { club_id: this.clubActual.id } });
          this.recargarPlan();
        } catch (error) {
          console.error('Error al eliminar la actividad:', error);
          alert(error.response.data.error || 'Error al eliminar la actividad');
        }
      }
    },
    async eliminarPlan() {
      if (confirm('¿Estás seguro de eliminar este plan?')) {
        try {
          await axios.delete(`/api/planes_cultivo/${this.planEditable.id}`, { data: { club_id: this.clubActual.id } });
          alert('Plan eliminado exitosamente');

          this.$emit('plan-eliminado', this.planEditable.id);
          this.planEditable = null; // Limpia referencia local

        } catch (error) {
          console.error('Error al eliminar el plan:', error);
          alert(error.response.data.error || 'Error al eliminar el plan');
        }
      }
    },
    cancelarEdicion() {
      this.modoEdicion = false;
    },

    etapaColor(index) {
      return index === 0 ? '#6c757d' : index % 2 === 0 ? 'var(--app-primary)' : '#198754';
    },
    cambiarEtapa(index) {
      this.etapaActual = index;
    },
    async recargarPlan() {
      try {
        const { data } = await axios.get(`/api/planes_cultivo/${this.planEditable.id}`, {
          params: { club_id: this.clubActual.id },
        });

        const actividadesOrdenadas = data.plan_cultivo_actividades
          .map((actividad) => ({
            ...actividad,
            numero_etapa: parseInt(actividad.numero_etapa, 10),
          }))
          .sort((a, b) => a.numero_etapa - b.numero_etapa);

        // Sobreescribe de manera controlada:
        this.planEditable.plan_cultivo_actividades = actividadesOrdenadas;

      } catch (error) {
        console.error('Error al recargar el plan:', error);
        alert(error.response.data.error || 'Error al recargar el plan');
      }
    },
    async fetchEventosCategorias() {
      try {
        const [eventosResponse, categoriasResponse] = await Promise.all([
          axios.get('/api/eventos'),
          axios.get('/api/categorias', { params: { club_id: this.clubActual.id } }),
        ]);
        this.eventos = eventosResponse.data;
        this.categorias = categoriasResponse.data;
      } catch (error) {
        console.error('Error al cargar eventos y categorías:', error);
        alert(error.response.data.error || 'Error al cargar eventos y categorías');
      }
    },
    toggleFormularioNuevaActividad() {
      this.mostrarFormularioNuevaActividad = !this.mostrarFormularioNuevaActividad;
    },
    cerrarModal() {
      this.mostrarFormularioNuevaActividad = false;
    },
    agregarActividad(nuevaActividad) {
      this.planEditable.plan_cultivo_actividades.push(nuevaActividad);
      this.cerrarModal();
      //fetch actividades
      this.recargarPlan();
    },

  },
  mounted() {
    this.clubActual = this.$store.getters.getClubActual;
    this.fetchEventosCategorias();
    emitter.on('cancelarActividad', () => {
      this.cerrarModal();
    });
  },
  beforeUnmount() {
    emitter.off('cancelarActividad');
  },
  watch: {
    plan: {
      immediate: true,
      handler(newPlan) {
        if (newPlan) {
          this.planEditable = { ...newPlan };
        } else {
          this.planEditable = null;
        }
      },
    },
    planEditable: {
      deep: true,
      handler(newVal) {
        if (newVal) {
          emitter.emit('ultimoNumeroEtapa', this.ultimoNumeroEtapa);
        }
        if (!newVal) {
          this.$emit('plan-eliminado'); // Asegura el refresco en el padre
        }
      },
    },
  },
};
</script>










<style scoped>
.container-fluid {
  background-color: #ffffff;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  padding: 1.5rem;
}

h4 {
  font-size: 1.5rem;
}

h5 {
  font-size: 1.25rem;
}

.d-grid.gap-3 {
  margin-top: 1rem;
}

.etapa-titulo {
  font-weight: 600;
  font-size: 1.05rem;
  color: #444;
}


@media (max-width: 768px) {
  .container-fluid {
    padding: 1rem;
  }
}

.etapa-titulo {
  font-size: 1.15rem;
  font-weight: 600;
  color: #343a40;
  border-bottom: 2px solid var(--bs-success);
  padding-bottom: 0.25rem;
}

.tareas-por-etapa {
  margin-top: 1rem;
}

.tareas-por-etapa .badge {
  font-size: 0.75rem;
}
</style>
